// Transient progress step the backend streams as `data-remy-step` while it
// works. Delivered to `useChat`'s `onData`, never persisted into message
// history.
export interface RemyStep {
  kind: 'tool' | 'model' | 'info'
  label: string
  status?: 'start' | 'end' | 'error'
  detail?: string
}

export interface ActivityStep {
  label: string
  active: boolean
}

// Human-readable narration for the raw backend step labels so the user sees
// what Remy is doing ("Searching the web…") instead of internals
// ("ChatOpenAI", "warehouse_query").
export const THINKING_LABEL = 'Thinking'

const TOOL_LABELS: Record<string, string> = {
  knowledge_search: 'Reviewing internal knowledge base',
  warehouse_query: 'Querying the data warehouse',
  read_file: 'Reading documents',
  grep: 'Searching the codebase',
  glob: 'Exploring the codebase',
  ls: 'Scanning filesystem',
  web_search: 'Searching the web',
  web_fetch: 'Reading a web page',
}

const humanizeLabel = (label: string) =>
  label.replace(/[_-]+/g, ' ').replace(/\b\w/g, char => char.toUpperCase())

const stepLabel = (step: RemyStep): string =>
  step.kind === 'model' ? THINKING_LABEL : (TOOL_LABELS[step.label] ?? humanizeLabel(step.label))

// Collapses the noisy interleaved step stream into a readable narrative: one row
// per distinct action in order, with the many interim "Thinking" steps reduced
// to the single live one trailing the list (the others are just the model
// handing off to the next tool). Only the last row is shown as in-progress.
export const toActivitySteps = (steps: RemyStep[]): ActivityStep[] => {
  const ordered: ActivityStep[] = []
  steps.forEach(step => {
    // The timeline is driven by "start" events; "end" only flips a row to done.
    if (step.status === 'end') return
    const label = stepLabel(step)
    if (ordered[ordered.length - 1]?.label !== label) ordered.push({ label, active: false })
  })

  const visible = ordered.filter(
    (step, index) => step.label !== THINKING_LABEL || index === ordered.length - 1,
  )
  // Dropping the interim Thinking rows can leave duplicates adjacent — re-collapse.
  const merged = visible.filter((step, index) => visible[index - 1]?.label !== step.label)

  // Always show at least a live "Thinking…" so the progress bubble is never blank
  // (covers the gap before any step lands, or unrecognized backend data).
  if (!merged.length) return [{ label: THINKING_LABEL, active: true }]

  merged[merged.length - 1].active = true
  return merged
}
